import {
  Button,
  CardBranch,
  IconButton,
  type IconName,
  Text
} from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import type { IPickupId } from '@/Domain/PickupId';
import {
  isDni,
  isRut
} from '@/modules/shared/types/IdentificationDocumentType';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import type { GtmGroupsData } from '@/modules/shared/utils/gtmEvents/types';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';

export interface PickupItem {
  title: string;
  cta?: {
    iconName: IconName;
    label: string;
    onClick: () => void;
  };
  description1Label?: string;
  description1Value?: string;
  description2?: string;
  radioButton?: {
    id: string;
    name: string;
    value: string;
    onChange: () => void;
    checked?: boolean;
  };
  isFree?: boolean;
  selected?: boolean;
  shippingSubType?: 'HOME' | 'IN_STORE' | 'COPEC';
  shippingType?: 'DELIVERY' | 'COLLECT';
  shippingMethod?:
    | 'SCHEDULED'
    | 'NEXT_DAY'
    | 'SAME_DAY'
    | 'DATE_RANGE'
    | 'SCHEDULED_DEFERRED';
  originNodeCode?: string;
  originNodeType?: string;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  hasCommuneOption?: boolean;
  collectNodeCode?: string;
  price?: string;
  crossedOutPrice?: string;
  textLink?: {
    label: string;
    onClick?: () => void;
  };
  infoTagLabel?: string;
  brand?:
    | 'paris'
    | 'paris-p'
    | 'jumbo'
    | 'easy'
    | 'sisa'
    | 'puntos'
    | 'copec'
    | 'novios-paris'
    | 'paris-125';
  showBorder?: boolean;
}

export interface PickUpSelectProps {
  deliveryGroupId?: string;
  title: string;
  pickupId?: IPickupId;
  onEditPickupId?: VoidFunction;
  onDeletePickupId?: VoidFunction;
  onModalOpen: (card: PickupItem, type: 'paris' | 'copec') => void;
  button: {
    label: string;
    onClick: () => void;
    iconName: IconName;
  };
  cardList?: PickupItem[];
  selectedCard?: string;
  gtmData?: GtmGroupsData;
  skus?: string;
  showCommuneStoreAlert?: boolean;
}

const PickUpSelect: React.FC<PickUpSelectProps> = ({
  title,
  deliveryGroupId,
  pickupId,
  onEditPickupId,
  onDeletePickupId,
  onModalOpen,
  button,
  cardList,
  selectedCard,
  gtmData,
  skus,
  showCommuneStoreAlert
}) => {
  const handleDeletePickupId = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': ShippingEventInteractiveTags.ClickTrashButton,
        'event-interactive-accion':
          ShippingEventInteractiveActions.AnotherPerson,
        'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
      }
    });

    onDeletePickupId && onDeletePickupId();
  };
  return (
    <div
      className="co-flex co-flex-col co-w-full"
      data-testid="co-pickup-select"
    >
      <div className="co-pt-6 co-px-4 co-pb-4">
        <Text variant="body2" weight={600}>
          {title}
        </Text>
      </div>
      <div className="co-w-full">
        {cardList?.map((card, index) => (
          <div
            key={index}
            className={classNames(
              'co-w-full co-border-t co-border-t-neutral-300'
            )}
          >
            <CardBranch
              {...card}
              oldPrice={card.crossedOutPrice}
              brand={undefined}
              cta={
                card.cta && {
                  ...card.cta,
                  onClick: () => {
                    card.cta?.onClick();
                    const type = card.brand === 'copec' ? 'copec' : 'paris';
                    if (showCommuneStoreAlert) {
                      if (type === 'copec') {
                        SendShippingEvents.sendEvent({
                          event: 'baseEvent',
                          extraFields: {
                            'event-interactive-accion':
                              ShippingEventInteractiveActions.NotAvailableStores,
                            'event-interactive-tag':
                              ShippingEventInteractiveTags.ClickChooseAnotherCopec,
                            'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
                          }
                        });
                      } else {
                        SendShippingEvents.sendEvent({
                          event: 'baseEvent',
                          extraFields: {
                            'event-interactive-accion':
                              ShippingEventInteractiveActions.NotAvailableStores,
                            'event-interactive-tag':
                              ShippingEventInteractiveTags.ClickChooseAnotherStore,
                            'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
                          }
                        });
                      }
                    }
                    if (type === 'copec') {
                      SendShippingEvents.sendEvent({
                        event: 'baseEvent',
                        extraFields: {
                          'event-interactive-accion':
                            ShippingEventInteractiveActions.ChooseAnotherCopec,
                          'event-interactive-tag': card.title,
                          'event-interactive-sku': skus || ''
                        }
                      });
                    }
                    const selectedCardBody = cardList.find(
                      (card) => card.collectNodeCode === selectedCard
                    );
                    onModalOpen(selectedCardBody as PickupItem, type);
                  }
                }
              }
              radioButton={
                card.radioButton && {
                  ...card.radioButton,
                  name: `${deliveryGroupId}-${title}`,
                  checked: card.radioButton.value === selectedCard
                }
              }
            />
          </div>
        ))}
      </div>
      <div className="co-px-3 co-py-4 co-border-t co-border-t-neutral-300">
        {!pickupId && (
          <Button
            variant="secondary"
            iconLeft={button.iconName}
            onClick={button.onClick}
            block
          >
            {button.label}
          </Button>
        )}
        {pickupId && (
          <div className="co-flex co-justify-between">
            <div className="co-flex co-flex-col">
              <div className="co-flex co-gap-1">
                <Text variant="caption" weight={600}>
                  {pickupId.fullname}
                </Text>
                <Text variant="caption" weight={400}>
                  retirará la compra
                </Text>
              </div>
              {isRut(pickupId.documentType) && (
                <Text variant="caption">RUT: {pickupId.documentID}</Text>
              )}
              {isDni(pickupId.documentType) && (
                <Text variant="caption">DNI: {pickupId.documentID}</Text>
              )}
              {!pickupId.documentType && pickupId.rut && (
                <Text variant="caption">RUT: {pickupId.rut}</Text>
              )}
            </div>
            <div className="co-flex co-gap-1">
              <IconButton
                icon="edit"
                variant="secondary"
                feedback="neutral"
                size="sm"
                onClick={onEditPickupId}
              />
              <IconButton
                icon="delete"
                variant="secondary"
                feedback="neutral"
                size="sm"
                onClick={handleDeletePickupId}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PickUpSelect;
